'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetPostStackUnitLoadHeightDetailsQuery, useCreateUnitLoadHeightsMutation, useGetPostStackUnitLoadHeightUniqueWeeksQuery } from '@/redux/slices/preship/preship-api-slice';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { refetchAfterSave } from "@/lib/refetchAfterSave";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { ChevronDown, Info } from "lucide-react";
import { setPalletLeanWeeks } from "@/redux/slices/pallet/palletSlice";
import { Tabs, TabsList } from "@/components/ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { endStatus } from "@/lib/utils";
import { Button } from "../ui/button";
import UnitLoadHeightConsolidatedView from "../common-components/UnitLoadHeightConsolidatedView";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { StackUnitLoadHeightService } from "@/services/StackUnitLoadHeightService";
import { Skeleton } from "@/components/ui/skeleton";
import EditBanner from "../common-components/EditBanner";

interface UnitLoadHeight {
  id?: number;
  variant: string;
  weekType: string;
  heightAB: string;
  heightBC: string;
  heightCD: string;
  heightDA: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;
  unitLoadHeights: UnitLoadHeight[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

const palletStackLeanWeeks = [
  "Stack start(stacked+30m)",
  "Stack Week-1",
  "Stack Week-2",
  "Stack Week-3",
  "Stack Week-4 (unstacking+30M)"
];
const disabledWeeks = [{
  label: "Pre-Ship",
  value: "Pre Ship"
}, {
  label: "Post-Ship",
  value: "Post Ship"
},
{ label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
{ label: "Stack Week-1", value: "Stack Week-1" },
{ label: "Stack Week-2", value: "Stack Week-2" },
{ label: "Stack Week-3", value: "Stack Week-3" },
]
const tabWeeks = ['Post stack unit load height (with load still on)', 'Post stack unit load with Rebound (30 min post load removal)']
const StackUnitLoadHeight: React.FC<{
  variant: string,

  saveCurrentForm?: any,
  hasUnsavedChanges?: boolean,
  onFormChange?: (hasChanges: boolean) => void;
  setHasUnsavedChanges?: any,
  isEditing?: boolean,
  isOffline?: boolean,
  setIsLoading?: any
}> = ({ variant, saveCurrentForm, onFormChange, activeWeek, setActiveWeek, hasUnsavedChanges, setHasUnsavedChanges, palletId, tabName, stage, isOffline, setIsLoading }) => {
  const { activePallet, palletLeanWeeks } = useSelector((state: RootState) => state.pallets);

  const { data: unitLoadHeightData, isLoading, error, refetch } = useGetPostStackUnitLoadHeightDetailsQuery({ palletId: activePallet?.id, variant, weekType: activeWeek }, {
    refetchOnMountOrArgChange: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const { data: uniqueWeeks, refetch: refetchWeeks, isFetching: uniqueWeeksFetching } = useGetPostStackUnitLoadHeightUniqueWeeksQuery({ palletId: activePallet?.id, variant }, { refetchOnMountOrArgChange: true })
  const [createUnitLoadHeights] = useCreateUnitLoadHeightsMutation();
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  const [offlineUnitLoadHeightData, setOfflineUnitLoadHeightData] = useState<any>(null);
  const [offlineUniqueWeeks, setOfflineUniqueWeeks] = useState<any>(null);
  const dispatch = useDispatch();
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [week, setWeek] = useState<string[]>([]);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const saveUnitLoadHeights = async (values: FormValues): Promise<boolean> => {
    try {
      if (!isOffline) {
        const payload = {
          palletId: activePallet?.id || 0,
          palletLpnNumbers: values.palletLpnNumbers.map(pln => ({
            ...pln,
            unitLoadHeights: pln.unitLoadHeights.map(ulh => ({
              ...ulh,
              variant: variant,
              weekType: activeWeek
            })),
          })),
        };
        await createUnitLoadHeights(payload).unwrap();
      } else {
        await StackUnitLoadHeightService.createStackUnitLoadHeights({ palletId: Number(activePallet?.id), palletLpnNumbers: values.palletLpnNumbers })
      }
    } catch (error) {
      showErrorToast("Failed to save unit load heights");
      console.error('Failed to save unit load heights:', error);
      return false;
    }

    showSuccessToast("Successfully saved unit load heights!");
    if (!isOffline) {
      // The save may finish after this tab unmounted; refreshing must not report it as failed.
      refetchAfterSave(refetch);
      refetchAfterSave(refetchWeeks);
    }
    return true;
  };

  const markValuesAsSaved = (values: FormValues) => {
    setInitialValues(JSON.parse(JSON.stringify(values)) as FormValues);
    onFormChange?.(false);
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    onSubmit: async (values) => {
      if (await saveUnitLoadHeights(values)) {
        markValuesAsSaved(values);
      }
    },
  });

  // Switching week tabs reloads the table, so unsaved edits for the current week are saved first.
  const handleWeekSelection = async (nextWeek: string) => {
    if (nextWeek === activeWeek) return;

    const hasLocalChanges = initialValues !== null
      && JSON.stringify(formik.values) !== JSON.stringify(initialValues);

    if (hasLocalChanges) {
      const valuesToSave = JSON.parse(JSON.stringify(formik.values)) as FormValues;
      if (!(await saveUnitLoadHeights(valuesToSave))) return;
      markValuesAsSaved(valuesToSave);
    }

    setActiveWeek(nextWeek);
  };
  useEffect(() => {
    setIsLoading(formik.isSubmitting)
  }, [formik.isSubmitting, setIsLoading])
  useEffect(() => {
    if (isOffline && activeWeek) {
      const getData = async () => {
        const res = await StackUnitLoadHeightService.getStackUnitLoadHeightDetails(Number(activePallet?.id), variant, activeWeek)
        setOfflineUnitLoadHeightData(res)
      }
      getData()
    }
  }, [isOffline, activeWeek])

  useEffect(() => {
    if (activePallet && !isLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (error && 'status' in error && error.status === 404) {
        initialPalletLpnNumbers = Array.from({ length: Number(activePallet.numberOfPallets) }, (_, index): PalletLpnNumber => ({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            weekType: activeWeek || '',
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      } else if (unitLoadHeightData && unitLoadHeightData.palletLpnNumbers && unitLoadHeightData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = unitLoadHeightData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',
          unitLoadHeights: pln.unitLoadHeights.length > 0 ? pln.unitLoadHeights : [{
            variant: variant,
            weekType: activeWeek || '',
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            weekType: activeWeek || '',
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [unitLoadHeightData, activePallet, error, isLoading, activeWeek, variant, activePallet?.numberOfPallets, uniqueWeeks]);
  useEffect(() => {
    if (activePallet && isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineUnitLoadHeightData && offlineUnitLoadHeightData.palletLpnNumbers && offlineUnitLoadHeightData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = offlineUnitLoadHeightData.palletLpnNumbers.map((pln: any) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',
          unitLoadHeights: pln.unitLoadHeights.length > 0 ? pln.unitLoadHeights : [{
            variant: variant,
            weekType: activeWeek || '',
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        }));
      }

      while (initialPalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        initialPalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          unitLoadHeights: [{
            variant: variant,
            weekType: activeWeek || '',
            heightAB: '',
            heightBC: '',
            heightCD: '',
            heightDA: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: initialPalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [isOffline, offlineUnitLoadHeightData, activePallet, activeWeek])
  useEffect(() => {
    if (activePallet) {
      refetch();
    }
  }, [activePallet?.numberOfPallets, refetch]);
  const handleCheckedChange = (isChecked: boolean, week: string) => {
    if (isChecked) {
      setAvailableWeeks([...availableWeeks, week])
    } else {
      setAvailableWeeks(availableWeeks.filter((w: any) => w !== week))
    }
  };
  useEffect(() => {
    if (activeWeek === null) {
      setActiveWeek(tabWeeks[0])
    }
  }, [])
  useEffect(() => {
    if (!isOffline) {
      refetchWeeks()
    } else {
      const getData = async () => {
        const res = await StackUnitLoadHeightService.getStackUnitLoadHeightUniqueWeeks(Number(activePallet?.id), variant)
        setOfflineUniqueWeeks(res)
      }
      getData()
    }
  }, [activePallet, variant,]);
  useEffect(() => {
    console.log('rann?', 'useEffect')
    if (!isOffline) {
      if (uniqueWeeks?.weeks) {
        setAvailableWeeks(uniqueWeeks.weeks.includes('Post stack unit load with Rebound (30 min post load removal)') ? uniqueWeeks.weeks : [...uniqueWeeks.weeks, 'Post stack unit load with Rebound (30 min post load removal)'])
        // setActiveWeek(tabWeeks[0])
      }
    } else {
      console.log('rann? isOffline', offlineUniqueWeeks)
      if (offlineUniqueWeeks?.weeks) {
        console.log('rann?', offlineUniqueWeeks)
        setAvailableWeeks(offlineUniqueWeeks.weeks)
        // setActiveWeek(tabWeeks[0])
      }
    }
  }, [uniqueWeeks, isOffline, offlineUniqueWeeks])
  console.log(availableWeeks, 'availableWeeks', offlineUniqueWeeks, 'offlineUniqueWeeks', isOffline)
  const endStatusOptions = [
    "Post stack unit load height (with load still on)",
    "Post stack unit load with Rebound (30 min post load removal)"
  ];
  useEffect(() => {
    const index = endStatusOptions.findIndex(item => item === activeWeek);
    const selectedItems = endStatusOptions.slice(index);
    setWeek(selectedItems);
  }, [activeWeek])
  // useEffect(() => {

  //     setActiveWeek(tabWeeks[0])

  // }, [])
  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])

  // Add arrow key navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp': targetRow = Math.max(0, rowIndex - 1); break;
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
      case 'ArrowDown': targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1); break;
      case 'ArrowLeft': targetCol = Math.max(2, colIndex - 1); break;
      case 'ArrowRight': targetCol = Math.min(6, colIndex + 1); break;
      default: return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`) as HTMLElement | null;
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  // Add roundToNearestQuarter function to match the Lean component
  const roundToNearestQuarter = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num * 4) / 4;
    return rounded.toFixed(2);
  };

  return (
    <div className=" space-y-2">
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      {(isLoading || uniqueWeeksFetching) ?
        <div className="space-y-6 w-full animate-pulse">
          {/* Header skeleton */}
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-32" />
          </div>

          {/* Tabs skeleton */}
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-32 rounded-md" />
            ))}
            <Skeleton className="h-10 w-64 rounded-md" />
          </div>

          {/* Table header skeleton */}
          <div className="border rounded-md overflow-hidden">
            <div className="border-b bg-gray-50">
              <div className="grid grid-cols-7 p-3">
                <Skeleton className="h-6 w-full mx-2" />
                {Array(6).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full mx-2" />
                ))}
              </div>
            </div>

            {/* Table rows skeleton */}
            <div className="p-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="grid grid-cols-7 gap-2 p-2 border-b last:border-0">
                  <Skeleton className="h-10 w-full" />
                  {Array(6).fill(0).map((_, j) => (
                    <Skeleton key={j} className="h-10 w-full" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div> :
        <div className={` flex flex-col gap-2`}>
          <Tabs defaultValue="Pallet Stack Lean Week 1" className="">
            <TabsList className="flex justify-start gap-3 bg-white">
              {tabWeeks.map((week: string, i: number) => (
                <div
                  onClick={() => void handleWeekSelection(week)}
                  key={i}
                  className={`${activeWeek === week ? "border border-blue bg-light-blue font-semibold text-blue" : "bg-gray-200 font-semibold"} cursor-pointer text-sm rounded-md p-2`}
                >
                  {week}
                </div>
              ))}
              {/* <DropdownMenu>
                <DropdownMenuTrigger className="w-[300px] rounded-xl border border-blue text-blue font-semibold data-[state=open]:border-blue data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none data-[state=open]:border-b data-[state=open]:border-b-border-style-1 py-3 text-sm text-left px-3 relative">
                  Select options
                  <ChevronDown size={20} className="text-blue absolute right-2 top-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[300px] data-[state=open]:border-blue border data-[state=open]:rounded-tl-none data-[state=open]:rounded-tr-none data-[state=open]:border-t-0">
                  {endStatusOptions.map((item: string, _i: number) => (
                    <div key={_i} className="flex items-center first:border-t-0 border-t border-border-style-1 justify-start gap-2 p-2 hover:bg-light-blue">
                      <Checkbox
                        checked={availableWeeks.includes(item)}
                        onCheckedChange={(checked: boolean) => handleCheckedChange(checked, item)}
                        className="rounded-full"
                        id={item}
                      />
                      <label htmlFor={item} className="text-sm font-medium py-1 leading-none peer-disabled:cursor-not-allowed cursor-pointer peer-disabled:opacity-70">
                        {item}
                      </label>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu> */}
            </TabsList>
          </Tabs>
          {(activePallet && activeWeek) && (
            <form id={`stack-unit-load-height-table-${activePallet?.id}`} onSubmit={formik.handleSubmit}>
              <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={3}>
                      Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
                    </TableHead>
                    <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={4}>

                    {!isOffline &&  <div className=' !pointer-events-auto flex items-center gap-2 justify-between'>
                        <span className="text-xs flex items-center gap-2 text-blue"><Info className="w-4 h-4" /> Note: Data will be rounded to the nearest 0.25 inch</span>
                        <div className="flex-center gap-4">
                          {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                  Clear All
                </Button> */}
                          <Button
                            onClick={() => setIsConsolidatedViewOpen(true)}
                            type='button' className="bg-white hover:bg-blue hover:text-white text-md font-semibold text-blue border-2 border-blue">
                            Consolidated View
                          </Button>
                        </div>
                      </div>}
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    {["Pallet No", "Pallet Identifier", "End Status", "A-B (inch)", "B-C (inch)", "C-D (inch)", "D-A (inch)"].map((head) => (
                      <TableHead
                        key={head}
                        className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left"
                      >
                        {head}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formik.values.palletLpnNumbers.map((pln, index) => {
                    const isRowEditable = !disabledWeeks.some(week => week.value === pln.endStatusPallet)
                    return (
                      <TableRow key={index}>
                        <TableCell className="border border-gray-300 bg-states-disabled p-0">
                          <Input
                            className="w-full h-9 border-none"
                            value={index + 1}
                            readOnly
                          />
                        </TableCell>
                        <TableCell className="border border-gray-300 bg-states-disabled p-0">
                          <Input
                            className="w-full border-none "
                            value={pln.lpnNo}
                            readOnly
                          />
                        </TableCell>
                        <TableCell className="border border-gray-300 p-0">
                          <Select
                            value={pln.endStatusPallet || undefined}
                            onValueChange={(value) => {
                              if (value !== '') {
                                formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value)
                              }
                              console.log(index)
                            }}
                          >
                            <SelectTrigger
                              data-row={index} data-col={2}
                              onKeyDown={(e) => handleKeyDown(e, index, 2)}
                              className="w-full rounded-none bg-transparent focus:border-blue"
                            >
                              <SelectValue placeholder="Select End Status" />
                            </SelectTrigger>
                            <SelectContent>
                              {endStatus.map((status) => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {['heightAB', 'heightBC', 'heightCD', 'heightDA'].map((field, fieldIndex) => {
                          const colIndexValue = 3 + fieldIndex;
                          return (
                            <TableCell key={field} className="border border-gray-300 p-0">
                              <Input
                                data-row={index} data-col={colIndexValue}
                                onKeyDown={(e) => handleKeyDown(e, index, colIndexValue)}
                                className={cn("w-full h-9 border-none focus:border-blue", !isRowEditable && "bg-gray-200")}
                                type="number"
                                step="0.25"
                                placeholder="Enter"
                                value={formik.values.palletLpnNumbers[index].unitLoadHeights[0][field] as string}
                                onChange={(e) => {
                                  // Allow free typing without rounding during input
                                  if (isRowEditable) {
                                    formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadHeights.0.${field}`, e.target.value);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Only round when user finishes typing (on blur)
                                  if (isRowEditable && e.target.value) {
                                    const roundedValue = roundToNearestQuarter(e.target.value);
                                    formik.setFieldValue(`palletLpnNumbers.${index}.unitLoadHeights.0.${field}`, roundedValue);
                                  }
                                }}
                                readOnly={!isRowEditable}
                                disabled={!isRowEditable}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <button type="submit" style={{ display: 'none' }} title="Submit Unit Load Height Data"></button>
              <UnitLoadHeightConsolidatedView
                isOpen={isConsolidatedViewOpen}
                onClose={() => setIsConsolidatedViewOpen(false)}
                variant={variant}
                activeWeek={activeWeek}
              />
            </form>
          )}
        </div>
      }
    </div>
  );
};

export default StackUnitLoadHeight;
